import { SubscriptionProductId } from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useQueryClient } from '@tanstack/react-query';
import { Redirect, router } from 'expo-router';
import { useSetAtom } from 'jotai';
import { ScreenContainer, Spinner, YStack } from 'smoothie';

import { isPremiumWelcomeSheetOpenAtom } from '../../../../billing/components/PremiumWelcomeSheet';
import UpgradeCheckout from '../../../../billing/components/UpgradeCheckout';
import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { FeatureFlags } from '../../../../feature-flags/FeatureFlags';
import useGetSubscription from '../../../../settings/hooks/useGetSubscription';
import { useUser } from '../../../../user/providers/UserProvider';

const LEGACY_PRODUCT_IDS: SubscriptionProductId[] = [
    SubscriptionProductId.SOLO,
    SubscriptionProductId.JOINT,
    SubscriptionProductId.ESSENTIAL,
    SubscriptionProductId.PLUS,
];

export default function UpgradeToPremiumPage() {
    const user = useAuthedUser();
    const { refetch: refetchUser } = useUser();
    const queryClient = useQueryClient();
    const setIsWelcomeSheetOpen = useSetAtom(isPremiumWelcomeSheetOpenAtom);
    const { data: subscription, isLoading: isSubscriptionLoading } =
        useGetSubscription({ userId: user.id });
    const { value: isPremiumMembershipsEnabled } = useFeatureGate(
        FeatureFlags.ENABLE_PREMIUM_MEMBERSHIPS,
    );

    if (!isPremiumMembershipsEnabled) {
        return <Redirect href="/settings/membership" />;
    }

    if (isSubscriptionLoading) {
        return (
            <ScreenContainer
                title="Upgrade to Premium"
                hasBackButton
                onFallbackRoute="/settings/membership"
            >
                <Spinner />
            </ScreenContainer>
        );
    }

    const productId = subscription?.product?.id;
    if (productId != null && LEGACY_PRODUCT_IDS.includes(productId)) {
        return <Redirect href="/settings/membership" />;
    }

    return (
        <ScreenContainer
            title="Upgrade to Premium"
            hasBackButton
            onFallbackRoute="/settings/membership"
        >
            <YStack gap={16} width="100%">
                <UpgradeCheckout
                    onSuccess={() => {
                        void refetchUser?.();
                        void queryClient.invalidateQueries({
                            queryKey: [`getSubscription/${user.id}`],
                        });
                        void queryClient.invalidateQueries({
                            queryKey: [`getPaymentMethod/${user.id}`],
                        });
                        if (router.canGoBack()) {
                            router.back();
                        } else {
                            router.replace('/settings/subscription');
                        }
                        setIsWelcomeSheetOpen(true);
                    }}
                />
            </YStack>
        </ScreenContainer>
    );
}
